import { useLiveQuery } from 'dexie-react-hooks';
import { BarChart3, History, LogOut, Pencil, Plus, Trash2, Undo2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { activeWithdrawal, dryOffDue, expectedCalving, sessionAverage } from '@dairy/domain';
import { addDays, diffDays, eachDay, type EventRecord, type SaleRecord } from '@dairy/shared';
import { useIsOwner, useProfile, useSettings } from '@/auth/useSession';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { toast } from '@/components/toast';
import {
  Badge,
  Button,
  Card,
  CardTitle,
  EmptyState,
  PageHeader,
  Table,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Td,
  Th,
  buttonVariants,
} from '@/components/ui';
import { errorMessage } from '@/components/errors';
import { db } from '@/db/db';
import { isLocked, saveMany, type Change } from '@/db/repo';
import { useHerd, useToday, useUsers } from '@/db/hooks';
import { useAge, useFormat } from '@/lib/format';
import { reminderChangesFor } from '@/features/events/effects';
import { EventSummary } from '@/features/events/EventSummary';
import { ReminderList } from '@/features/reminders/RemindersPage';
import { Photo, ReproBadge, StageBadge } from './common';

const BREEDING_TYPES = ['HEAT', 'SERVICE', 'PREG_CHECK', 'CALVING', 'DRY_OFF', 'ABORTION'];
const HEALTH_TYPES = ['TREATMENT', 'VACCINATION', 'DEWORMING', 'WEIGHT', 'NOTE'];

/** Screen 8 — Animal profile (AR-6): header, timeline and tabs (Milk S2; Breeding, Health, Reminders S3; Money S4). */
export function AnimalProfilePage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const f = useFormat();
  const age = useAge();
  const s = useSettings();
  const profile = useProfile();
  const isOwner = useIsOwner();
  const today = useToday();
  const navigate = useNavigate();
  const herd = useHerd(today);
  const users = useUsers();
  const row = herd.byId.get(id);
  const events = useLiveQuery(() => db.events.where('animalId').equals(id).toArray(), [id]) ?? [];
  const milk = useLiveQuery(() => db.milkRecords.where('animalId').equals(id).toArray(), [id]) ?? [];
  const reminders = useLiveQuery(() => db.reminders.where('animalId').equals(id).toArray(), [id]) ?? [];
  const expenses = useLiveQuery(() => db.expenses.where('animalId').equals(id).toArray(), [id]) ?? [];
  const sale = useLiveQuery(() => db.sales.where('animalId').equals(id).first(), [id]) as SaleRecord | undefined;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmUndo, setConfirmUndo] = useState(false);

  const sortedEvents = useMemo(() => [...events].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : (b.clientCreatedAt ?? '').localeCompare(a.clientCreatedAt ?? ''))), [events]);

  if (!row) return herd.loading ? null : <EmptyState>{t('animals.notFound')}</EmptyState>;
  const a = row.animal;
  const exited = a.status !== 'ACTIVE';
  const lact = row.lact;
  const lactOpen = lact?.open && !exited;

  // Milk tab figures
  const last30 = eachDay(addDays(today, -29), today).map((d) => ({
    date: d,
    liters: milk.filter((r) => r.date === d).reduce((acc, r) => acc + r.ml, 0) / 1000,
  }));
  const last7 = milk.filter((r) => r.date >= addDays(today, -7) && r.date < today);
  const days7 = new Set(last7.map((r) => r.date)).size;
  const avg7 = days7 ? last7.reduce((acc, r) => acc + r.ml, 0) / days7 : null;
  const lactTotal = lact ? milk.filter((r) => r.date >= lact.calvingDate && (!lact.endDate || r.date <= lact.endDate)).reduce((acc, r) => acc + r.ml, 0) : 0;
  const withdrawal = activeWithdrawal(row.events, today);
  const deps = events.filter((e) => !e.isOpening).length + milk.length + expenses.length + (sale ? 1 : 0);

  const doDelete = async () => {
    if (deps > 0) {
      toast(t('errors.HAS_DEPENDENTS'), 'error');
      return;
    }
    const changes: Change[] = [
      ...reminders.map((r): Change => ({ entity: 'reminder', op: 'delete', id: r.id })),
      ...events.map((e): Change => ({ entity: 'event', op: 'delete', id: e.id })),
      { entity: 'animal', op: 'delete', id: a.id },
    ];
    try {
      await saveMany(changes);
      toast(t('common.deleted'));
      navigate('/animals', { replace: true });
    } catch (e) {
      toast(errorMessage(t, e), 'error');
    }
  };

  const undoExit = async (deleteSale: boolean) => {
    const restored = { ...a, status: 'ACTIVE' as const, exitDate: null, exitNote: null };
    const changes: Change[] = [{ entity: 'animal', op: 'upsert', record: restored }, ...(await reminderChangesFor(restored, row.events))];
    if (deleteSale && sale) changes.push({ entity: 'sale', op: 'delete', id: sale.id });
    await saveMany(changes);
    toast(t('common.saved'));
  };

  const canEdit = isOwner || (a.source === 'BORN_ON_FARM' && a.createdBy === profile.userId && !isLocked(a, profile, s));

  return (
    <div className="space-y-4">
      <PageHeader title={a.tag} />
      <Card className="flex gap-4">
        {a.photoFileId ? <Photo fileId={a.photoFileId} className="h-24 w-24 shrink-0 rounded-2xl" alt={a.tag} /> : null}
        <div className="min-w-0 flex-1 space-y-2">
          <div className="text-lg font-semibold">{a.name ?? '—'}</div>
          <div className="flex flex-wrap gap-2">
            <StageBadge stage={a.status !== 'ACTIVE' ? a.status : row.stage} />
            {!exited ? <ReproBadge status={row.repro?.status} /> : null}
            {withdrawal ? <Badge tone="amber">{t('health.withdrawalUntil', { date: f.short(withdrawal.until) })}</Badge> : null}
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
            <div>
              <dt className="text-stone-600">{t('animals.age.label')}</dt>
              <dd className="font-medium">{age(a.birthDate, today)}</dd>
            </div>
            {a.sex === 'FEMALE' ? (
              <div>
                <dt className="text-stone-600">{t('animals.lactation')}</dt>
                <dd className="font-medium">{lact?.number ?? a.openingParity}</dd>
              </div>
            ) : null}
            {lactOpen ? (
              <div>
                <dt className="text-stone-600">{t('animals.dim')}</dt>
                <dd className="font-medium">{lact!.dim}</dd>
              </div>
            ) : null}
            <div>
              <dt className="text-stone-600">{t('animals.breed')}</dt>
              <dd className="font-medium">{a.breed}</dd>
            </div>
          </dl>
        </div>
      </Card>

      <div className="flex flex-wrap gap-2">
        {!exited ? (
          <Link to={`/events/new?animalId=${a.id}`} className={buttonVariants({ size: 'sm' })}>
            <Plus className="h-4 w-4" /> {t('events.record')}
          </Link>
        ) : null}
        {canEdit ? (
          <Link to={`/animals/${a.id}/edit`} className={buttonVariants({ size: 'sm', variant: 'secondary' })}>
            <Pencil className="h-4 w-4" /> {t('common.edit')}
          </Link>
        ) : null}
        {!exited ? (
          <Link to={`/animals/${a.id}/exit`} className={buttonVariants({ size: 'sm', variant: 'secondary' })}>
            <LogOut className="h-4 w-4" /> {t('animals.exit.action')}
          </Link>
        ) : isOwner ? (
          <Button size="sm" variant="secondary" onClick={() => setConfirmUndo(true)}>
            <Undo2 className="h-4 w-4" /> {t('animals.exit.undo')}
          </Button>
        ) : null}
        {isOwner ? (
          <>
            <Link to={`/reports/cows/${a.id}`} className={buttonVariants({ size: 'sm', variant: 'secondary' })}>
              <BarChart3 className="h-4 w-4" /> {t('reports.cowReport')}
            </Link>
            <Link to={`/history/animal/${a.id}`} className={buttonVariants({ size: 'sm', variant: 'secondary' })}>
              <History className="h-4 w-4" /> {t('history.title')}
            </Link>
            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="h-4 w-4" /> {t('common.delete')}
            </Button>
          </>
        ) : null}
      </div>

      <Tabs defaultValue="timeline">
        <TabsList>
          <TabsTrigger value="timeline">{t('animals.tabs.timeline')}</TabsTrigger>
          {a.sex === 'FEMALE' ? <TabsTrigger value="milk">{t('animals.tabs.milk')}</TabsTrigger> : null}
          {a.sex === 'FEMALE' ? <TabsTrigger value="breeding">{t('animals.tabs.breeding')}</TabsTrigger> : null}
          <TabsTrigger value="health">{t('animals.tabs.health')}</TabsTrigger>
          <TabsTrigger value="reminders">{t('animals.tabs.reminders')}</TabsTrigger>
          {isOwner ? <TabsTrigger value="money">{t('animals.tabs.money')}</TabsTrigger> : null}
        </TabsList>

        <TabsContent value="timeline">
          <Card>
            <ul className="divide-y divide-stone-100">
              {exited ? (
                <li className="py-3">
                  <div className="font-semibold">{t(`animals.exit.type.${a.status}`)}</div>
                  <div className="text-sm text-stone-600">
                    {f.date(a.exitDate)} · {a.exitNote}
                  </div>
                </li>
              ) : null}
              {sortedEvents.map((e) => (
                <EventRow key={e.id} event={e} userName={users.get(e.createdBy)?.name} />
              ))}
              <li className="py-3">
                <div className="font-semibold">{a.source === 'BORN_ON_FARM' ? t('animals.timeline.born') : t('animals.timeline.registered')}</div>
                <div className="text-sm text-stone-600">
                  {a.source === 'BORN_ON_FARM' ? f.date(a.birthDate) : f.date(a.clientCreatedAt?.slice(0, 10))}
                </div>
              </li>
            </ul>
          </Card>
        </TabsContent>

        <TabsContent value="milk">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Card className="p-3">
                <div className="text-sm text-stone-600">{t('animals.lactation')}</div>
                <div className="text-xl font-bold">{lact?.number ?? '—'}</div>
              </Card>
              <Card className="p-3">
                <div className="text-sm text-stone-600">{t('animals.dim')}</div>
                <div className="text-xl font-bold">{lactOpen ? lact!.dim : '—'}</div>
              </Card>
              <Card className="p-3">
                <div className="text-sm text-stone-600">{t('milk.avg7')}</div>
                <div className="text-xl font-bold">{avg7 === null ? '—' : f.liters(Math.round(avg7))}</div>
              </Card>
              <Card className="p-3">
                <div className="text-sm text-stone-600">{t('milk.lactationTotal')}</div>
                <div className="text-xl font-bold">{f.liters(lactTotal)}</div>
              </Card>
            </div>
            <Card>
              <CardTitle>{t('milk.last30')}</CardTitle>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={last30} margin={{ left: -20, right: 8, top: 8 }}>
                    <CartesianGrid stroke="#e7e5e4" />
                    <XAxis dataKey="date" tickFormatter={(d: string) => d.slice(8)} fontSize={12} />
                    <YAxis fontSize={12} />
                    <Tooltip formatter={(v) => `${Number(v).toFixed(1)} ${t('units.l')}`} labelFormatter={(d) => f.date(String(d))} />
                    <Line type="monotone" dataKey="liters" stroke="#166534" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <Table className="mt-3">
                <thead>
                  <tr>
                    <Th>{t('common.date')}</Th>
                    <Th className="text-right">{t('milk.produced')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {[...last30].reverse().filter((d) => d.liters > 0).map((d) => (
                    <tr key={d.date}>
                      <Td>{f.short(d.date)}</Td>
                      <Td className="text-right tabular-nums">{d.liters.toFixed(1)}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <p className="mt-2 text-sm text-stone-500">
                {t('milk.morningAvg', { liters: f.liters(Math.round(sessionAverage(milk, a.id, 'MORNING', today).avgMl)) })}
              </p>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="breeding">
          <div className="space-y-4">
            <Card>
              <CardTitle>{t('breeding.statusCard')}</CardTitle>
              <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
                <Fact label={t('breeding.status')} value={row.repro ? t(`animals.repro.${row.repro.status}`) : '—'} />
                <Fact label={t('breeding.lastService')} value={f.date(row.repro?.lastServiceDate)} />
                <Fact label={t('breeding.daysSinceService')} value={row.repro?.lastServiceDate ? String(diffDays(today, row.repro.lastServiceDate)) : '—'} />
                <Fact label={t('breeding.expectedCalving')} value={f.date(row.repro?.expectedCalving)} />
                <Fact label={t('breeding.dryOffDue')} value={f.date(row.repro?.dryOffDue)} />
                <Fact label={t('breeding.daysSinceCalving')} value={lact ? String(lact.dim) : '—'} />
              </dl>
              {row.repro?.status === 'PREGNANT' && row.repro.lastServiceDate ? (
                <p className="mt-3 text-sm text-stone-600">
                  {t('breeding.datesFromService', {
                    exp: f.date(expectedCalving(row.repro.lastServiceDate, s)),
                    dry: f.date(dryOffDue(expectedCalving(row.repro.lastServiceDate, s), s)),
                  })}
                </p>
              ) : null}
            </Card>
            <Card>
              <CardTitle>{t('breeding.history')}</CardTitle>
              <ul className="divide-y divide-stone-100">
                {sortedEvents.filter((e) => BREEDING_TYPES.includes(e.type)).map((e) => (
                  <EventRow key={e.id} event={e} userName={users.get(e.createdBy)?.name} />
                ))}
              </ul>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="health">
          <div className="space-y-4">
            {withdrawal ? (
              <div className="rounded-2xl bg-amber-100 p-4 font-medium text-amber-950">{t('health.withdrawalBanner', { date: f.date(withdrawal.until) })}</div>
            ) : null}
            <Card>
              <ul className="divide-y divide-stone-100">
                {sortedEvents.filter((e) => HEALTH_TYPES.includes(e.type)).map((e) => (
                  <EventRow key={e.id} event={e} userName={users.get(e.createdBy)?.name} />
                ))}
              </ul>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="reminders">
          <ReminderList reminders={reminders.filter((r) => r.status === 'OPEN')} today={today} />
          <h3 className="mb-2 mt-4 font-semibold text-stone-700">{t('reminders.recentlyClosed')}</h3>
          <ReminderList
            reminders={reminders
              .filter((r) => r.status !== 'OPEN' && (r.completedAt ?? '') >= addDays(today, -30))
              .sort((x, y) => ((x.completedAt ?? '') < (y.completedAt ?? '') ? 1 : -1))}
            today={today}
            closed
          />
        </TabsContent>

        {isOwner ? (
          <TabsContent value="money">
            <Card>
              <CardTitle>{t('animals.money.linkedExpenses')}</CardTitle>
              <ul className="divide-y divide-stone-100">
                {expenses.map((e) => (
                  <li key={e.id} className="flex min-h-12 items-center justify-between gap-2 py-2">
                    <Link to={`/expenses/${e.id}/edit`} className="flex-1">
                      {f.short(e.date)} {e.description ?? ''}
                      {e.sourceEventId ? <Badge className="ml-2">{t('expenses.fromEvent')}</Badge> : null}
                    </Link>
                    <span className="tabular-nums">{f.birr(e.amountSantim)}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-2 flex justify-between border-t border-stone-200 pt-2 font-bold">
                <span>{t('common.total')}</span>
                <span>{f.birr(expenses.reduce((acc, e) => acc + e.amountSantim, 0))}</span>
              </div>
              {sale ? (
                <p className="mt-3">
                  {t('animals.money.sale', { date: f.date(sale.date), amount: f.birr(sale.amountSantim) })}
                </p>
              ) : null}
            </Card>
          </TabsContent>
        ) : null}
      </Tabs>

      <ConfirmDialog open={confirmDelete} onOpenChange={setConfirmDelete} title={t('animals.deleteTitle')} danger confirmLabel={t('common.delete')} onConfirm={doDelete}>
        <p>{deps > 0 ? t('animals.deleteHasRecords') : t('animals.deleteBody', { tag: a.tag })}</p>
      </ConfirmDialog>
      <ConfirmDialog
        open={confirmUndo}
        onOpenChange={setConfirmUndo}
        title={t('animals.exit.undoTitle')}
        confirmLabel={sale ? t('animals.exit.undoAndDeleteSale') : t('animals.exit.undo')}
        onConfirm={() => undoExit(true)}
      >
        <p>{sale ? t('animals.exit.undoSaleQuestion') : t('animals.exit.undoBody')}</p>
        {sale ? (
          <Button variant="secondary" className="mt-3" onClick={() => void undoExit(false).then(() => setConfirmUndo(false))}>
            {t('animals.exit.undoKeepSale')}
          </Button>
        ) : null}
      </ConfirmDialog>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-stone-600">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

function EventRow({ event, userName }: { event: EventRecord; userName?: string }) {
  return (
    <li>
      <Link to={`/events/${event.id}/edit`} className="block min-h-12 py-3">
        <EventSummary event={event} userName={userName} />
      </Link>
    </li>
  );
}
